import { API_BASE_URL, apiFetch, ApiError } from './httpClient';
import { SessionStatusDto, UploadConfig, UploadResultDto } from '../core/types';

export interface InitSessionRequest {
  projectName: string;
  fileName: string;
  fileSize: number;
  relativePath?: string;
  batchId?: string;
  totalChunks: number;
  chunkSize: number;
  clientMimeType?: string;
}

export function getUploadConfig(): Promise<UploadConfig> {
  return apiFetch<UploadConfig>('/uploads/config');
}

export function initSession(req: InitSessionRequest): Promise<SessionStatusDto> {
  return apiFetch<SessionStatusDto>('/uploads/sessions', {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

export function getSessionStatus(uploadId: string): Promise<SessionStatusDto> {
  return apiFetch<SessionStatusDto>(`/uploads/sessions/${uploadId}`);
}

export function completeUpload(uploadId: string, clientSha256?: string): Promise<UploadResultDto> {
  return apiFetch<UploadResultDto>(`/uploads/sessions/${uploadId}/complete`, {
    method: 'POST',
    body: JSON.stringify({ clientSha256 }),
  });
}

export function cancelSession(uploadId: string): Promise<void> {
  return apiFetch<void>(`/uploads/sessions/${uploadId}`, { method: 'DELETE' });
}

export function listProjectFiles(projectName: string): Promise<UploadResultDto[]> {
  return apiFetch<UploadResultDto[]>(`/uploads/files/${encodeURIComponent(projectName)}`);
}

export function deleteProjectFile(projectName: string, absolutePath: string): Promise<void> {
  return apiFetch<void>(`/uploads/files/${encodeURIComponent(projectName)}`, {
    method: 'DELETE',
    body: JSON.stringify({ absolutePath }),
  });
}

export function moveProjectFile(
  projectName: string,
  absolutePath: string,
  newRelativePath: string,
): Promise<UploadResultDto> {
  return apiFetch<UploadResultDto>(`/uploads/files/${encodeURIComponent(projectName)}`, {
    method: 'PATCH',
    body: JSON.stringify({ absolutePath, newRelativePath }),
  });
}

export interface ChunkUploadHandle {
  promise: Promise<SessionStatusDto>;
  abort: () => void;
}

/**
 * Uses XMLHttpRequest (not fetch) because it's the only browser API that
 * exposes upload progress events, which per-file/overall speed and ETA
 * depend on.
 */
export function uploadChunk(
  uploadId: string,
  chunkIndex: number,
  blob: Blob,
  onProgress: (loadedBytes: number) => void,
): ChunkUploadHandle {
  const xhr = new XMLHttpRequest();
  const formData = new FormData();
  formData.append('chunk', blob, `chunk-${chunkIndex}`);

  const promise = new Promise<SessionStatusDto>((resolve, reject) => {
    xhr.open('PUT', `${API_BASE_URL}/uploads/sessions/${uploadId}/chunks/${chunkIndex}`);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded);
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as SessionStatusDto);
        } catch {
          reject(new Error('Malformed response from server'));
        }
        return;
      }
      reject(new ApiError(parseXhrErrorMessage(xhr), xhr.status));
    };

    xhr.onerror = () => reject(new Error('Network error while uploading chunk'));
    xhr.onabort = () => {
      const abortError = new Error('Upload aborted');
      abortError.name = 'AbortError';
      reject(abortError);
    };

    xhr.send(formData);
  });

  return { promise, abort: () => xhr.abort() };
}

function parseXhrErrorMessage(xhr: XMLHttpRequest): string {
  try {
    const body = JSON.parse(xhr.responseText);
    if (Array.isArray(body?.message)) return body.message.join(', ');
    if (typeof body?.message === 'string') return body.message;
  } catch {
    // fall through
  }
  return `Chunk upload failed with status ${xhr.status}`;
}
